import { notification } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import React from "react";
import { TableState, TableStateFunctions } from "@/types/tableState.types";
import {
  tableStateToWorkspaceConfig,
  workspaceConfigToTableState,
} from "@/utils/workspaceState";
import {
  Workspace,
  WorkspaceList,
  WorkspaceMutations,
} from "@/components/UI/TableToolbar/WorkspaceButton/workspace";
import { WorkspaceConfig } from "@/types/workspaceConfig";
import { isSingleSubjectPage, sortWorkspaces } from "@/utils/workspaceUtils";

type UseWorkspaceActionsParams = {
  tableName: string;
  workspaces: WorkspaceList;
  currentWorkspace: Workspace | null;
  setCurrentWorkspace: (workspaceId: number) => void;
  mutations: WorkspaceMutations;
  initialTableState: TableState;
  tableStateFunctions: TableStateFunctions;
  metadata?: {
    columns?: { field: string; label: string }[];
    filters?: Record<string, unknown>;
  };
  onDrawerClose?: () => void;
};

const showNotification = (title: string) => {
  notification.open({
    title,
    placement: "bottom",
    duration: 3,
    icon: React.createElement(CheckCircleFilled, {
      style: { color: "#52c41a" },
    }),
    className: "workspace-success-notification",
  });
};

export const useWorkspaceActions = ({
  tableName,
  workspaces,
  currentWorkspace,
  setCurrentWorkspace,
  mutations,
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
  initialTableState,
  tableStateFunctions,
  metadata,
  onDrawerClose,
}: UseWorkspaceActionsParams) => {
  const metadataColumns = metadata?.columns?.map((c) => c.field);
  const metadataFilterKeys = Object.keys(metadata?.filters || {});
  const isSSJ = isSingleSubjectPage(tableName);
  const excludeFilterKeys = isSSJ ? ["subject"] : [];

  const restoreConfiguration = (configuration: WorkspaceConfig) => {
    const tableState = workspaceConfigToTableState(
      configuration,
      metadataColumns,
      metadataFilterKeys,
    );

    let filtersToRestore = tableState.filters || {};
    if (isSSJ && filtersToRestore.subject) {
      /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
      const { subject, ...otherFilters } = filtersToRestore;
      filtersToRestore = otherFilters;
    }

    tableStateFunctions.reset();
    tableStateFunctions.setFilters(filtersToRestore);
    tableStateFunctions.setColumn(tableState.columns || []);
    tableStateFunctions.setLockedColumns(tableState.lockedColumns || 0);
    tableStateFunctions.setPlots(tableState.plots || []);
    tableStateFunctions.setPlotLayoutsByBreakpoint(
      tableState.plotLayoutsByBreakpoint || {},
    );
    tableStateFunctions.setScreenView(tableState.screenView || "table");
    tableStateFunctions.setSorting(tableState.sorting || []);
    tableStateFunctions.setPagination({
      current: 1,
      pageSize: tableState.pagination?.pageSize || 100,
      total: 0,
    });
  };

  const handleSelectWorkspace = (workspaceId: number) => {
    setCurrentWorkspace(workspaceId);
    const workspace = workspaces.find((w) => w.id === workspaceId);
    if (workspace) {
      restoreConfiguration(workspace.configuration);
    }
    onDrawerClose?.();
  };

  const handleDuplicate = async (
    title: string,
    configuration: WorkspaceConfig,
    isVivoTemplate?: boolean,
  ) => {
    const newWorkspace = await mutations.createWorkspaceAsync({
      title,
      table_name: tableName,
      configuration,
      is_vivo_template: isVivoTemplate || false,
    });
    setCurrentWorkspace(newWorkspace.id);
    restoreConfiguration(configuration);
    showNotification(`Workspace duplicated as "${title}"`);
  };

  const handleRename = (
    workspaceId: number,
    newTitle: string,
    isVivoTemplate?: boolean,
  ) => {
    mutations.updateWorkspace({
      workspaceId,
      payload: {
        title: newTitle,
        // Only superusers get the template checkbox; omit the field otherwise
        // so the backend permission check for non-superusers isn't triggered.
        ...(isVivoTemplate !== undefined && {
          is_vivo_template: isVivoTemplate,
        }),
      },
    });
  };

  const handleDelete = (workspaceId: number) => {
    const workspace = workspaces.find((w) => w.id === workspaceId);
    const wasCurrent = currentWorkspace?.id === workspaceId;
    mutations.deleteWorkspace(workspaceId);

    if (wasCurrent) {
      const remaining = sortWorkspaces(
        workspaces.filter((w) => w.id !== workspaceId),
      );
      const firstWorkspace = remaining[0];
      if (firstWorkspace) {
        setCurrentWorkspace(firstWorkspace.id);
        restoreConfiguration(firstWorkspace.configuration);
      }
    }

    showNotification(
      workspace?.title
        ? `Workspace "${workspace.title}" has been deleted`
        : "Workspace has been deleted",
    );
  };

  const handleCreateNew = async (
    title: string,
    configuration: WorkspaceConfig,
    isVivoTemplate?: boolean,
  ) => {
    const newWorkspace = await mutations.createWorkspaceAsync({
      title,
      table_name: tableName,
      configuration,
      is_vivo_template: isVivoTemplate || false,
    });
    setCurrentWorkspace(newWorkspace.id);
    restoreConfiguration(configuration);
    onDrawerClose?.();
    showNotification(`Workspace "${title}" created`);
  };

  const handleSaveWorkspace = async (currentTableState: TableState) => {
    if (currentWorkspace) {
      const updatedWorkspace = await mutations.updateWorkspaceAsync({
        workspaceId: currentWorkspace.id,
        payload: {
          configuration: tableStateToWorkspaceConfig(
            currentTableState,
            excludeFilterKeys,
          ),
        },
      });
      tableStateFunctions.setWorkspaceMeta(
        updatedWorkspace.id,
        updatedWorkspace.modified_at,
      );
      showNotification(
        `Your changes to "${currentWorkspace.title}" have been successfully saved`,
      );
    }
  };

  const handleSaveAsNewWorkspace = async (
    name: string,
    currentTableState: TableState,
    isVivoTemplate?: boolean,
  ) => {
    const newWorkspace = await mutations.createWorkspaceAsync({
      title: name,
      table_name: tableName,
      configuration: tableStateToWorkspaceConfig(
        currentTableState,
        excludeFilterKeys,
      ),
      is_vivo_template: isVivoTemplate || false,
    });
    setCurrentWorkspace(newWorkspace.id);
    showNotification(`Saved as new workspace "${name}"`);
  };

  return {
    handleSelectWorkspace,
    handleDuplicate,
    handleRename,
    handleDelete,
    handleCreateNew,
    handleSaveWorkspace,
    handleSaveAsNewWorkspace,
  };
};
